import { CheckboxLabeled } from '@/design-system/checkbox/checkbox-labeled';
import { VerticalScrollArea } from '@/design-system/vertical-scroll-area/vertical-scroll-area';
import { formatPercentage } from '@/utils/formatting/format-percentage';
import { tw } from '@/utils/tw';
import { ArrowDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { ANNUAL_FEE_PERCENTAGE } from '../treasury-page';

const GB_TERMS_AND_CONDITIONS_URL = 'https://www.seapoint.co/terms-and-conditions';

type KiidAcceptanceModalContentProps = {
  name: string;
  netYield: number;
  kiidLink: string;
  prospectusLink: string;
  formattedUpdatedAtByFundManager: string;
  isLoading: boolean;
  onContinue: () => Promise<void>;
  onClose: () => void;
};

export const KiidAcceptanceModalContent = ({
  name,
  netYield,
  kiidLink,
  prospectusLink,
  formattedUpdatedAtByFundManager,
  isLoading,
  onContinue,
  onClose,
}: KiidAcceptanceModalContentProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isCheckboxVisible, setIsCheckboxVisible] = useState(false);
  const checkboxRef = useRef<HTMLDivElement>(null);

  const handleScrollToBottom = () => {
    checkboxRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formattedNetYield = formatPercentage(netYield);

  const headingClassName = tw`text-sm font-semibold text-foreground`;
  const paragraphClassName = tw`text-sm font-normal text-muted-foreground`;
  const linkClassName = tw`text-sm font-normal text-muted-foreground underline decoration-solid decoration-auto underline-offset-auto`;

  useEffect(() => {
    const element = checkboxRef.current;

    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsCheckboxVisible(entry.isIntersecting),
      { threshold: 0.5 }, // 50% visible
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <Stack space={6}>
      <VerticalScrollArea className="max-h-96">
        <Stack space={6}>
          <Stack space={2}>
            <h3 className={headingClassName}>You're investing in a fund that holds government-guaranteed assets.</h3>

            <p className={paragraphClassName}>
              When you use Interest, your money is invested in the {name}. This fund holds government-guaranteed assets,
              like bonds and loans. Although this type of fund is considered low risk, when investing your capital is at
              risk. The value of your investments can go up as well as down. You may get back less than you put in and
              you may lose all your initial investment
            </p>

            <p className={paragraphClassName}>
              You may have to pay tax on your earnings - for example, capital gains tax. Tax treatment depends on
              individual circumstances and may be subject to change in the future.
            </p>

            <p className={paragraphClassName}>
              This information does not constitute advice nor a recommendation. You should consider your own personal
              circumstances when making investment decisions. If you are unsure how to proceed, you should seek
              professional independent advice as to the suitability/appropriateness of any investment for your
              individual circumstances or needs.
            </p>
          </Stack>

          <Stack space={2}>
            <h3 className={headingClassName}>The fund has a {formattedNetYield} variable rate after annual fees</h3>

            <p className={paragraphClassName}>
              The current variable rate is based on the 7-day performance as of {formattedUpdatedAtByFundManager} and
              factors in our annual fee of {formatPercentage(ANNUAL_FEE_PERCENTAGE)}.
            </p>

            <p className={paragraphClassName}>
              Our treasury product is provided by Seapoint Finance UK Investments Ltd, in partnership with WealthKernel.
              Seapoint Finance UK Investments Ltd (FRN: 1039246) is an appointed representative of Wealthkernel Limited,
              which is authorised and regulated by the Financial Conduct Authority (FRN: 723719).
            </p>
          </Stack>

          <Stack space={1}>
            <a href={GB_TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer" className={linkClassName}>
              Terms & conditions
            </a>

            <a href={kiidLink} target="_blank" rel="noopener noreferrer" className={linkClassName}>
              Key investor information document (PDF)
            </a>

            <a href={prospectusLink} target="_blank" rel="noopener noreferrer" className={linkClassName}>
              Fund prospectus (PDF)
            </a>
          </Stack>

          <div ref={checkboxRef}>
            <Inline space={2} align="start" wrap="nowrap">
              <CheckboxLabeled
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
                label="I have read and understand the above information and documents"
                labelClassName={tw`text-sm leading-[14px] font-normal text-foreground`}
              />
            </Inline>
          </div>
        </Stack>
      </VerticalScrollArea>

      <div className="flex w-full flex-1 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <AnimatePresence>
          {!isCheckboxVisible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                variant="link"
                iconLeft={<ArrowDownIcon />}
                onClick={handleScrollToBottom}
                className="shrink-0 px-0 text-muted-foreground"
              >
                Scroll to check details
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="secondary" onClick={onClose} isLoading={isLoading}>
            Cancel
          </Button>

          <Button variant="default" onClick={onContinue} isLoading={isLoading} disabled={!isChecked}>
            Open treasury account
          </Button>
        </div>
      </div>
    </Stack>
  );
};
